import { useState, useEffect, useCallback } from 'react';
import coreAPIService from '../apiServices/coreAPIService';
import { CTW_API_ENDPOINT } from '../utils';
import { toast } from '../common/Popover';
import { AdAccountsResponse, FbPagesResponse } from '../interfaces/apiTypes';
import { handleApiError } from '../utils/errorHandler';
import { useMetaSetup } from '../context/MetaSetupContext';
import { useAuthOrg } from '../context/AuthOrgContext';

interface AdAccount {
    id: string;
    name: string;
}

interface FbPage {
    id: string;
    name: string;
}

interface WabaAccount {
    wabaId: string;
    wabaNumber: string;
    clientId: string;
    phoneNumberId: string;
    subdomain: string;
}

export const useAdAccounts = (orgId?: string) => {
    const { selectedAdAccount, setSelectedAdAccount, selectedFbPage, setSelectedFbPage, selectedWaba, setSelectedWaba, orgWabaData } = useMetaSetup();
    const { currentOrg, availableOrgs } = useAuthOrg();

    const [adAccounts, setAdAccounts] = useState<AdAccount[]>([]);
    const [adAccountLoading, setAdAccountLoading] = useState(true);
    const [fbPages, setFbPages] = useState<FbPage[]>([]);
    const [pagesLoading, setPagesLoading] = useState(false);
    const [wabaList, setWabaList] = useState<WabaAccount[]>([]);
    const [isMilestoneCheckPassed, setIsMilestoneCheckPassed] = useState<boolean | null>(null);
    const [checkingMilestones, setCheckingMilestones] = useState(false);
    const [isMilestoneCheckErrorMsg, setIsMilestoneCheckErrorMsg] = useState<string | null>();

    // Derive IDs from context
    const selectedAdAccountId = selectedAdAccount?.id || '';
    const selectedFbPageId = selectedFbPage?.id || '';

    const checkMilestones = useCallback(async (clientId: string) => {
        if (!clientId) return;
        try {
            setCheckingMilestones(true);
            const response = await coreAPIService.get<any>(
                CTW_API_ENDPOINT,
                `v2/util/check-lead-milestones?client_id=${clientId}`
            );
            if (response.meta.status) {
                setIsMilestoneCheckPassed(response.data);
            } else {
                setIsMilestoneCheckPassed(false);
                setIsMilestoneCheckErrorMsg(response.meta.message);

            }
        } catch (error) {
            console.error('Error checking milestones:', error);
            setIsMilestoneCheckPassed(false);
        } finally {
            setCheckingMilestones(false);
        }
    }, []);

    // Load WABA from orgId or currentOrg context
    useEffect(() => {
        let wabaData: any[] = [];

        if (orgId && orgWabaData && orgWabaData.length > 0) {
            // Filter by provided orgId
            const targetOrg = orgWabaData.find(org => org.orgId === orgId);
            if (targetOrg && targetOrg.wabaDetails) {
                wabaData = targetOrg.wabaDetails.map((item: any) => ({
                    wabaId: item.wabaId || '',
                    wabaNumber: item.wabaNumber,
                    clientId: item.clientId || orgId,
                    phoneNumberId: item.phoneNumberId || '',
                    subdomain: item.subdomain || '',
                }));
            }
        } else {
            // Fallback to currentOrg context
            const orgWithWaba = currentOrg;
            if (orgWithWaba?.waba_numbers && Array.isArray(orgWithWaba.waba_numbers)) {
                wabaData = orgWithWaba.waba_numbers
                    .filter((item: any) => item.clientWaRegisteredNumber)
                    .map((item: any) => ({
                        wabaId: item.wabaId || '',
                        wabaNumber: item.clientWaRegisteredNumber,
                        clientId: item.clientId || currentOrg?.orgId || '',
                        phoneNumberId: item.phoneNumberId || '',
                    }));
            }
        }

        setWabaList(wabaData);

        // Auto-select first WABA if none selected or if current selection is not in the new list
        if (wabaData.length > 0) {
            const isCurrentWabaInList = wabaData.some(w => w.wabaNumber === selectedWaba?.wabaNumber);
            if (!selectedWaba || !isCurrentWabaInList) {
                const firstWaba = wabaData[0];
                setSelectedWaba({
                    clientId: firstWaba.clientId,
                    subdomain: '',
                    wabaId: firstWaba.wabaId,
                    wabaNumber: firstWaba.wabaNumber,
                    phoneNumberId: firstWaba.phoneNumberId,
                    metaConversionApiStatus: false,
                    accessTokenType: '',
                    datasetId: '',
                });
                checkMilestones(firstWaba.clientId);
            } else {
                // Check milestones for current selection if list changed
                const currentWaba = wabaData.find(w => w.wabaNumber === selectedWaba?.wabaNumber);
                if (currentWaba) {
                    checkMilestones(currentWaba.clientId);
                }
            }
        } else {
            setSelectedWaba(null);
            setIsMilestoneCheckPassed(null);
        }
    }, [orgId, orgWabaData, currentOrg, selectedWaba?.wabaNumber, setSelectedWaba]);

    // Fetch Ad Accounts
    useEffect(() => {
        const fetchAdAccounts = async () => {
            try {
                setAdAccountLoading(true);
                const response = await coreAPIService.get<AdAccountsResponse>(
                    CTW_API_ENDPOINT,
                    'v2/org/ad-accounts'
                );

                if (response.meta.status) {
                    const accounts = response.data || [];
                    setAdAccounts(accounts);

                    // Auto-select first account if none selected
                    if (accounts.length > 0 && !selectedAdAccount) {
                        setSelectedAdAccount({ id: accounts[0].id, name: accounts[0].name });
                    }
                }
            } catch (error) {
                handleApiError(
                    error,
                    'v2/org/ad-accounts',
                    'Failed to load ad accounts'
                );
            } finally {
                setAdAccountLoading(false);
            }
        };

        fetchAdAccounts();
    }, []);

    // Fetch FB Pages when Ad Account changes
    useEffect(() => {
        if (!selectedAdAccountId) {
            setFbPages([]);
            setSelectedFbPage(null);
            return;
        }

        const fetchFbPages = async () => {
            try {
                setPagesLoading(true);
                const response = await coreAPIService.get<FbPagesResponse>(
                    CTW_API_ENDPOINT,
                    `v2/org/fb-pages`
                );

                if (response.meta.status) {
                    const pages = response.data || [];
                    setFbPages(pages);

                    // Auto-select first page if none selected
                    if (pages.length > 0 && !selectedFbPage) {
                        setSelectedFbPage({ id: pages[0].id, name: pages[0].name });
                    }
                }
            } catch (error) {
                handleApiError(
                    error,
                    `v2/org/fb-pages`,
                    'Failed to load Facebook pages'
                );
            } finally {
                setPagesLoading(false);
            }
        };

        fetchFbPages();
    }, [selectedAdAccountId]);

    const handleAdAccountChange = useCallback((value: string, preserveSelections = false) => {
        const account = adAccounts.find(acc => acc.id === value);
        if (account) {
            setSelectedAdAccount({ id: account.id, name: account.name });
        }
        if (!preserveSelections) {
            setSelectedFbPage(null);
            setFbPages([]);
        }
    }, [adAccounts, setSelectedAdAccount, setSelectedFbPage]);

    const handleFbPageChange = useCallback((value: string) => {
        const page = fbPages.find(p => p.id === value);
        if (page) {
            setSelectedFbPage({ id: page.id, name: page.name });
        }
    }, [fbPages, setSelectedFbPage]);

    const handleWabaChange = useCallback((value: string) => {
        const waba = wabaList.find(w => w.wabaNumber === value);
        if (waba) {
            setSelectedWaba({
                clientId: waba.clientId,
                subdomain: '',
                wabaId: waba.wabaId,
                wabaNumber: waba.wabaNumber,
                phoneNumberId: waba.phoneNumberId,
                metaConversionApiStatus: false,
                accessTokenType: '',
                datasetId: '',
            });
            checkMilestones(waba.clientId);
        }
    }, [wabaList, setSelectedWaba, checkMilestones]);

    return {
        adAccounts,
        selectedAdAccountId,
        adAccountLoading,
        handleAdAccountChange,
        fbPages,
        selectedFbPageId,
        pagesLoading,
        handleFbPageChange,
        wabaList,
        selectedWaba: selectedWaba?.wabaNumber || null,
        handleWabaChange,
        selectedOrgId: currentOrg?.orgId || '',
        availableOrgs,
        isMilestoneCheckPassed,
        checkingMilestones,
        isMilestoneCheckErrorMsg
    };
};
